import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { Toaster } from 'react-hot-toast';

import { BrowserRouter } from 'react-router-dom';
import './index.css';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store/store';
import * as serviceWorker from './serviceWorker';
import ThemedSuspense from './components/ThemedSuspense';
import { Windmill } from '@windmill/react-ui';
import customTheme from './theme/customTheme';
import ErrorBoundary from './pages/ErrorBoundary';
import VersionChecker, { APP_VERSION } from './utils/versionChecker';
import App from './App';
import './utils/requestLogger'; // Debug: logs every axios & fetch request/response
import { SidebarProvider } from './context/SidebarContext';
import { ApplicationContextProvider } from './context/ApplicationContext';
import { BenefitsRequestProvider } from './context/BenefitsRequestContext';
import { ClientContextProvider } from './context/ClientContext';
import { AgentContextProvider } from './context/AgentContext';
import { ProposalDetailsProvider } from './context/ProposalDetailsContext';
import { PaymentDataContextProvider } from './context/PaymentDataContext';
import { SignatureContextProvider } from './context/SignatureContext';
import { TrusteeContextProvider } from './context/TrusteeContext';
import { BeneficiaryContextProvider } from './context/BeneficiaryContext';
import { CheckboxProvider } from './context/CheckboxContext';
import { ProductDetailsContextProvider } from './context/ProductDetailsContext';
import { WebsiteUserProvider } from './context/WebsiteUserContext';
import { InitialsProvider } from './context/InitialsContext';
import { ThemeProvider } from './context/ThemeContext';

const resizeObserverWarning = /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;

const preventResizeObserverWarning = (event) => {
  const message = event.message || event.reason?.message || String(event.reason || '');
  if (resizeObserverWarning.test(message)) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
};

window.addEventListener('error', preventResizeObserverWarning, true);
window.addEventListener('unhandledrejection', preventResizeObserverWarning, true);

if (typeof window.ResizeObserver !== 'undefined') {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  }
}

// Service worker update configuration
const serviceWorkerConfig = {
  onUpdate: (registration) => {
    console.log('Service worker update available');
    window.dispatchEvent(new CustomEvent('appUpdateAvailable', {
      detail: { registration, timestamp: Date.now() },
    }));
  },
  onSuccess: (registration) => {
    console.log('Service worker registered successfully');
    
    // Check for version updates
    const versionCheck = VersionChecker.checkForUpdates();
    if (versionCheck.hasUpdate) {
      console.log(`Version update detected via service worker: ${versionCheck.oldVersion} -> ${versionCheck.newVersion}`);
      
      // Trigger update notification
      const versionEvent = new CustomEvent('versionUpdate', {
        detail: versionCheck
      });
      window.dispatchEvent(versionEvent);
      
      // Update stored version
      VersionChecker.updateVersion();
    }
  }
};

// Check for updates on initial load
const initializeVersionCheck = async () => {
  try {
    console.log('Initializing app version:', APP_VERSION);
    
    // Check for updates
    const versionCheck = await VersionChecker.checkForUpdates();
    
    if (versionCheck.hasUpdate) {
      console.log(`Initial version update detected: ${versionCheck.oldVersion} -> ${versionCheck.newVersion}`);
      
      // Trigger update notification
      const versionEvent = new CustomEvent('versionUpdate', {
        detail: versionCheck
      });
      window.dispatchEvent(versionEvent);
      
      // Update stored version
      VersionChecker.updateVersion();
    } else {
      console.log('App is up to date:', versionCheck.currentVersion);
    }
  } catch (error) {
    console.error('Error during initial version check:', error);
  }
};

// Initialize version check
initializeVersionCheck();

// Wrapping everything inside a single Suspense with a fallback UI
ReactDOM.render(
  <ErrorBoundary>
    <Provider store={store}>
      <PersistGate loading={<ThemedSuspense />} persistor={persistor}>
        <ErrorBoundary>
          <Suspense fallback={<ThemedSuspense />}>
            <BrowserRouter>
              <ThemeProvider>
                <Windmill theme={customTheme}>
                <InitialsProvider>
                <SidebarProvider>
                  <ApplicationContextProvider>
                    <BenefitsRequestProvider>
                      <ClientContextProvider>
                        <AgentContextProvider>
                          <ProposalDetailsProvider>
                            <PaymentDataContextProvider>
                              <SignatureContextProvider>
                                <TrusteeContextProvider>
                                  <BeneficiaryContextProvider>
                                    <CheckboxProvider>
                                      <ProductDetailsContextProvider>
                                          <WebsiteUserProvider>
                                             <Toaster />
                                          <App />
                                        </WebsiteUserProvider>
                                      </ProductDetailsContextProvider>
                                    </CheckboxProvider>
                                  </BeneficiaryContextProvider>
                                </TrusteeContextProvider>
                              </SignatureContextProvider>
                            </PaymentDataContextProvider>
                          </ProposalDetailsProvider>
                        </AgentContextProvider>
                      </ClientContextProvider>
                    </BenefitsRequestProvider>
                  </ApplicationContextProvider>
                  </SidebarProvider>
                  </InitialsProvider>
                </Windmill>
              </ThemeProvider>
            </BrowserRouter>
          </Suspense>
        </ErrorBoundary>
      </PersistGate>
    </Provider>
  </ErrorBoundary>,
  document.getElementById('root')
);

// Register service worker with update config
serviceWorker.register(serviceWorkerConfig);

// Log version info
console.log(`%cStarLife Portal v${APP_VERSION}`, 'color: #752F87; font-size: 14px; font-weight: bold;');